import { isDefined, isNonEmpty } from '@sgme/fp';
import type { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import type { StaticPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { FormError } from 'models/formError';
import { AllowedRequests } from 'models/forms/FormResult';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import type { ProductGovernance, RegulatoryDocument } from 'models/streaming';
import { buildProductDescriptionKey, buildRemunerationMode, getInstrumentName } from 'state/common-form/form.helper';
import { formEnrichedDataReceived } from 'state/formData/formData.action';
import type { DataById } from 'state/formData/formData.model';
import { checkFormEnrichmentRequestNeededThunk } from 'state/formData/formData.thunks';
import { patchedFieldsComputed } from 'state/formData/patchedFields/patchedFields.actions';
import { computePatchedFieldsDiff } from 'state/formData/patchedFields/patchedFields.helper';
import { commonFormActions } from 'state/forms/actions/form';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { commonStreamActions } from 'state/forms/actions/stream';
import { commonTradeAction } from 'state/forms/actions/trade';
import { triggerDocumentationFetchThunk } from 'state/forms/documentation.thunks';
import {
  dispatchCommonFormActionsForWorkspace,
  tradeAuthorizationReceivedWithBackToBackInitThunk,
} from 'state/forms/thunks';
import type { StaticProductView } from 'state/product-description/types';
import {
  dashboardTileCreating,
  dashboardTileDuplicating,
  dashboardTileRemoved,
} from 'state/quotes/dashboard/dashboard.actions';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { QuoteType } from 'state/quotes/infos/infos.model';
import { pendingQuoteDetailsSaved } from 'state/quotes/pendingDetails/pendingDetails.action';
import { priceImprovementInitialized } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import { notUsedQuoteCleanedThunk } from 'state/quotes/quotes.thunks';
import {
  kidDocumentReceived,
  kidDocumentTimeout,
  productGovernanceAssessmentReceived,
  productGovernanceAssessmentTimeout,
  termsheetDocumentReceived,
} from 'state/quotes/regulations/regulation.actions';
import { isQuoteTypeWithRegulation } from 'state/quotes/utils';
import { getFormEnrichedData, getFormRequestType, getStaticFormQuoteId } from 'state/selectors/selectors';
import type { Thunk } from 'state/state.common';
import { updateSidePanelMetadata } from 'state/uiPanelState/sidePanelUi/sidePanelUi.action';
import type { TabsForWorkspace, WorkspaceType } from 'state/workspaces/model';
import { staticWorkspaceTabSetQuote } from 'state/workspaces/static/static.actions';
import { workspaceTabCloned, workspaceTabFrozen, workspaceTabUnfrozen } from 'state/workspaces/workspace.actions';

import type { FormBlockMetadataById } from '../../models/metadata/block.definition';
import type { QuoteAction } from '../common-form/infos/infos.model';
import type { FieldMetadataById } from '../common-form/metaData/fields/fields.model';

const dispatchCommonFormActions = dispatchCommonFormActionsForWorkspace('static');

export function defaultFormReceivedThunk(
  tabId: string,
  productView: StaticProductView,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  negotiationId: string | undefined,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
): Thunk<void> {
  return (dispatch, _getState) => {
    dispatch(
      commonFormActions.defaultStatifiedFormReceived(
        'static',
        tabId,
        productView,
        buildProductDescriptionKey(values),
        getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        negotiationId,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        mustForceAdvancedMode,
        validationErrors,
        sendWholeFormData,
      ),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);
    dispatch(triggerDocumentationFetchThunk(tabId, 'static'));
  };
}

export function staticTabReloadedThunk(tabId: string, tabContent: StaticPersistedTabContent): Thunk<void> {
  return (dispatch, _getState) => {
    const {
      negotiationId,
      blocksMetadata,
      fieldsMetadata,
      values,
      version,
      actions,
      quoteQuery,
      errors,
      productView,
      mustForceAdvancedMode,
      quoteId,
      sendWholeFormData,
      allowedRequests,
    } = tabContent;
    dispatch(
      staticFormReloadedThunk(
        tabId,
        productView,
        negotiationId,
        blocksMetadata,
        fieldsMetadata,
        values,
        version,
        actions,
        quoteQuery,
        mustForceAdvancedMode,
        allowedRequests,
        errors,
        sendWholeFormData,
      ),
    );
    if (quoteId !== undefined) {
      dispatch(commonQuoteAction.previousQuoteLoadRequested('static', tabId, quoteId));
    }
  };
}

export function staticFormReloadedThunk(
  tabId: string,
  productView: StaticProductView,
  negotiationId: string | undefined,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
): Thunk<void> {
  return (dispatch, _getState) => {
    const productDescription = buildProductDescriptionKey(values);
    const remunerationMode = buildRemunerationMode(values);
    dispatch(
      commonFormActions.statifiedFormReloaded('static', tabId, {
        productView,
        negotiationId,
        productDescription,
        instrumentName: getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        actions,
        quoteQuery,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        sendWholeFormData,
      }),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(triggerDocumentationFetchThunk(tabId, 'static'));
  };
}

export function workspaceTabClonedThunk<W extends WorkspaceType>(
  oldTabId: string,
  workspaceType: W,
  tabType: TabsForWorkspace[W],
): Thunk<void> {
  return (dispatch, getState, { getNewGuid, selectors: { getStaticFormInfos } }) => {
    const state = getState();
    const infos = getStaticFormInfos(state, oldTabId);
    const enrichedData = getFormEnrichedData(state, oldTabId);

    if (infos !== undefined) {
      const newTabId = getNewGuid();
      dispatch(
        workspaceTabCloned(
          oldTabId,
          newTabId,
          infos.negotiationId,
          workspaceType,
          tabType,
          enrichedData.ProductType as string,
          enrichedData.ProductSubtype as string,
        ),
      );
    }
  };
}

export function staticFormEnrichmentReceivedThunk(
  tabId: string,
  productView: StaticProductView,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const previousEnriched = getFormEnrichedData(state, tabId);
    const patched = computePatchedFieldsDiff(previousEnriched, values, fieldsMetadata);
    dispatch(patchedFieldsComputed(tabId, patched));

    dispatch(
      commonFormActions.statifiedFormEnrichmentReceived(
        'static',
        tabId,
        productView,
        buildProductDescriptionKey(values),
        getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        mustForceAdvancedMode,
        validationErrors,
      ),
    );
    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);
    dispatch(checkFormEnrichmentRequestNeededThunk(tabId, tabId, 'static', fieldsMetadata));
    dispatch(triggerDocumentationFetchThunk(tabId, 'static'));
  };
}

export function staticQuoteRequestFailedThunk(
  tabId: string,
  error: ApiErrorMessage,
  frontReconciliationId: string,
): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.quoteRequestFailed('static', tabId, error, frontReconciliationId));

    if (error.newVersion !== undefined) {
      dispatch(commonFormActions.updateRfqVersion('static', tabId, error.newVersion));
    }

    if (error.status === 400 && error.functionalErrorType === 'rfq-version-mismatch') {
      dispatch(commonFormActions.fetchFormEnrichmentFailed('static', tabId, error as Error));
    }
  };
}

export function staticQuoteRequestedThunk(tabId: string, quoteType: QuoteType, refreshedQuoteId?: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormQuoteId }, getNewGuid, getNow }) => {
    const state = getState();
    const quoteId = getStaticFormQuoteId(state, tabId);
    const frontReconciliationId = getNewGuid();
    const requestType = getFormRequestType(state, 'static', tabId);
    if (quoteId) {
      dispatch(staticQuoteRemovedThunk(tabId, quoteId));
    }
    if (refreshedQuoteId !== undefined) {
      dispatch(dashboardTileDuplicating(frontReconciliationId, refreshedQuoteId, 'static'));
    } else {
      dispatch(dashboardTileCreating(frontReconciliationId, 'static'));
    }
    const isReprice = refreshedQuoteId !== undefined;

    if (requestType === 'RequestForStream') {
      dispatch(commonStreamActions.streamRequested('static', tabId, quoteType, getNow().getTime(), isReprice));
    } else {
      dispatch(
        commonQuoteAction.quoteRequested(
          'static',
          tabId,
          quoteType,
          getNow().getTime(),
          frontReconciliationId,
          isReprice,
        ),
      );
    }

    dispatch(workspaceTabFrozen(tabId, 'dynamic', true));
  };
}

export function staticQuoteRemovedThunk(tabId: string, quoteId?: string): Thunk<void> {
  return (dispatch, getState) => {
    if (!isDefined(quoteId)) {
      const state = getState();
      quoteId = getStaticFormQuoteId(state, tabId);
    }

    if (isDefined(quoteId)) {
      dispatch(commonQuoteAction.quoteRemoved('static', tabId, quoteId));
      dispatch(notUsedQuoteCleanedThunk(tabId, quoteId));
    } else {
      dispatch(commonQuoteAction.quoteRemoved('static', tabId));
    }
  };
}

export function staticQuoteAckedThunk(
  tabId: string,
  quoteId: string,
  quoteType: QuoteType,
  externalQuoteId: string,
  negotiationId: string | undefined,
  estimatedQuotingDurationInMillis: number,
  quoteRequestStartingTimeInMillis: number,
  newVersion: number,
  isExecOnlyAddon: boolean,
  quoteDetails: QuoteDetailsData,
  frontReconciliationId: string,
  sidePanelDetails: readonly ReadonlyValueDefinition[] | undefined,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const isKidExpected = isQuoteTypeWithRegulation(quoteType);
    dispatch(
      commonQuoteAction.quoteAcked(
        'static',
        tabId,
        quoteId,
        quoteType,
        externalQuoteId,
        negotiationId,
        estimatedQuotingDurationInMillis,
        quoteRequestStartingTimeInMillis,
        newVersion,
        isExecOnlyAddon,
        isKidExpected,
        frontReconciliationId,
        quoteDetails,
        getNow(),
        sidePanelDetails,
      ),
    );

    dispatch(staticWorkspaceTabSetQuote(tabId, quoteId));
    dispatch(
      pendingQuoteDetailsSaved(quoteId, 'static', {
        start: new Date(quoteRequestStartingTimeInMillis),
        durationInMs: estimatedQuotingDurationInMillis,
      }),
    );
  };
}

export function staticStreamingQuoteAckedThunk(
  tabId: string,
  quoteId: string,
  quoteType: QuoteType,
  externalQuoteId: string,
  negotiationId: string | undefined,
  estimatedQuotingDurationInMillis: number,
  quoteRequestStartingTimeInMillis: number,
  newVersion: number,
  isExecOnlyAddon: boolean,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    dispatch(
      commonStreamActions.streamingQuoteAcked(
        'static',
        tabId,
        quoteId,
        quoteType,
        externalQuoteId,
        negotiationId,
        estimatedQuotingDurationInMillis,
        quoteRequestStartingTimeInMillis,
        newVersion,
        isExecOnlyAddon,
        getNow(),
      ),
    );

    dispatch(
      pendingQuoteDetailsSaved(quoteId, 'static', {
        start: new Date(quoteRequestStartingTimeInMillis),
        durationInMs: estimatedQuotingDurationInMillis,
      }),
    );
  };
}

export function quoteResultReceivedThunk(
  tabId: string,
  quoteId: string,
  data: QuoteResultDto<StaticFormResult>,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormInfos }, getNow }) => {
    const state = getState();

    const infos = getStaticFormInfos(state, tabId);
    const formResult = data.formResult;
    const isQuoteRejected = data.status === 'Rejected' || data.status === 'Failed';
    const when = getNow();

    dispatch(commonQuoteAction.quoteReceived('static', tabId, quoteId, data, when));
    dispatch(updateSidePanelMetadata(tabId, { tradeWay: data.tradeWay }));

    if (isQuoteRejected) {
      dispatch(workspaceTabUnfrozen(tabId, 'static', undefined));
    }

    dispatch(priceImprovementInitialized(quoteId));

    if (formResult !== undefined && infos !== undefined) {
      const {
        productView,
        values,
        blocksMetadata,
        fieldsMetadata,
        quoteQuery,
        actions,
        errors,
        version,
        mustForceAdvancedMode,
      } = formResult;
      const previousEnriched = getFormEnrichedData(state, tabId);
      const patched = computePatchedFieldsDiff(previousEnriched, values, fieldsMetadata);
      dispatch(patchedFieldsComputed(tabId, patched));

      dispatch(
        commonFormActions.statifiedFormEnrichmentReceived(
          'static',
          tabId,
          productView,
          infos.productDescription,
          infos.instrumentName,
          blocksMetadata,
          fieldsMetadata,
          version,
          actions,
          quoteQuery,
          infos.remunerationMode,
          mustForceAdvancedMode,
          errors,
        ),
      );
      dispatch(formEnrichedDataReceived(tabId, tabId, values, 'static'));
      dispatch(triggerDocumentationFetchThunk(tabId, 'static'));
    }
  };
}

export function staticFormTabRollbackedThunk(negotiationId: string, tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormQuoteId } }) => {
    const state = getState();
    const quoteId = getStaticFormQuoteId(state, tabId);

    if (quoteId) {
      dispatch(staticQuoteRemovedThunk(tabId, quoteId));
    }
    dispatch(commonFormActions.formTabRollbacked('static', tabId, negotiationId));
  };
}

export function staticQuoteTradeCancelledThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormInfos } }) => {
    const state = getState();
    const formInfos = getStaticFormInfos(state, tabId);
    const quoteId = formInfos?.quoteId;
    const productSubtype = getFormEnrichedData(state, tabId)?.['ProductSubtype'] as string | undefined;
    const productType = getFormEnrichedData(state, tabId)?.['ProductType'] as string | undefined;
    const requestType = getFormRequestType(state, 'static', tabId);
    if (requestType === 'RequestForStream') {
      dispatch(
        commonStreamActions.streamingCancelled(
          'static',
          tabId,
          'Tradable',
          formInfos?.negotiationId,
          quoteId,
          productSubtype,
          productType,
        ),
      );
      dispatch(workspaceTabUnfrozen(tabId, 'static', undefined));
    } else {
      dispatch(
        commonQuoteAction.quoteCancelled(
          'static',
          tabId,
          formInfos?.negotiationId,
          quoteId,
          productSubtype,
          productType,
        ),
      );
    }
    if (quoteId) {
      dispatch(commonQuoteAction.quoteStale('static', tabId, quoteId, false));
    }
  };
}

export function staticQuoteTradeRequestedThunk(tabId: string, quoteId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const requestType = getFormRequestType(state, 'static', tabId);
    if (!quoteId) {
      return;
    }
    if (requestType === 'RequestForStream') {
      dispatch(commonStreamActions.streamingTradeRequested('static', tabId, quoteId));
    } else {
      dispatch(commonTradeAction.quoteTradeRequested('static', tabId, quoteId));
    }
  };
}
export function staticQuoteCancelledThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormInfos } }) => {
    const state = getState();
    const formInfos = getStaticFormInfos(state, tabId);
    const quoteId = formInfos?.quoteId;
    const productSubtype = getFormEnrichedData(state, tabId)?.['ProductSubtype'] as string | undefined;
    const productType = getFormEnrichedData(state, tabId)?.['ProductType'] as string | undefined;
    const requestType = getFormRequestType(state, 'static', tabId);

    if (requestType === 'RequestForStream') {
      dispatch(
        commonStreamActions.streamingCancelled(
          'static',
          tabId,
          'Indicative',
          formInfos?.negotiationId,
          quoteId,
          productSubtype,
          productType,
        ),
      );
    } else {
      dispatch(
        commonQuoteAction.quoteCancelled(
          'static',
          tabId,
          formInfos?.negotiationId,
          quoteId,
          productSubtype,
          productType,
        ),
      );
    }
    if (quoteId) {
      dispatch(dashboardTileRemoved(quoteId));
      dispatch(staticQuoteRemovedThunk(tabId, quoteId));
    }
  };
}

export function staticKidDocumentReceivedThunk(
  tabId: string,
  quoteId: string,
  doc: RegulatoryDocument,
  replacePreviousResources: boolean = false,
): Thunk<void> {
  return dispatch => {
    dispatch(kidDocumentReceived('static', tabId, quoteId, doc, replacePreviousResources));
  };
}
export function staticTermsheetDocumentReceivedThunk(
  tabId: string,
  quoteId: string,
  doc: RegulatoryDocument,
  replacePreviousResources: boolean = false,
): Thunk<void> {
  return dispatch => {
    dispatch(termsheetDocumentReceived('static', tabId, quoteId, doc, replacePreviousResources));
  };
}

export function staticKidDocumentTimeoutThunk(tabId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(kidDocumentTimeout('static', tabId, quoteId));
  };
}

export function staticStreamTradeAuthReceivedThunk(tabId: string, quoteId: string): Thunk<void> {
  return (dispatch, _getState, { getNow }) => {
    const now = getNow();
    dispatch(commonStreamActions.streamingTradeAuthReceived('static', tabId, quoteId, now));
    dispatch(tradeAuthorizationReceivedWithBackToBackInitThunk('static', tabId, quoteId));
  };
}

export function staticProductGovernanceAssesmentReceivedThunk(
  tabId: string,
  quoteId: string,
  pg: ProductGovernance,
): Thunk<void> {
  return dispatch => {
    dispatch(productGovernanceAssessmentReceived('static', tabId, quoteId, pg));
  };
}

export function staticProductGovernanceAssesmentTimeoutThunk(tabId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(productGovernanceAssessmentTimeout('static', tabId, quoteId));
    dispatch(productGovernanceAssessmentReceived('static', tabId, quoteId, undefined));
  };
}
